import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getAllCategories,
  getAllSuppliers,
  getEstoqueSituacaoRelatorio,
} from '../../services/api';
import { getAuthPermissoes } from '../../services/authStorage';
import { useEstabelecimento } from '../../context/EstabelecimentoContext';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import SortableTh from '../common/SortableTh';
import { useClientSort } from '../../hooks/useClientSort';
import { getApiErrorMessage } from '../../utils/apiError';
import downloadCsv from '../../utils/downloadCsv';
import { normalizeSearchTerm } from '../../utils/productSearch';
import BuscaProdutoCampo from '../common/BuscaProdutoCampo';

const TIPOS = [
  { value: 'baixo', label: 'Estoque baixo (≤ mínimo)' },
  { value: 'abaixo_ideal', label: 'Abaixo do ideal' },
  { value: 'acima_maximo', label: 'Acima do máximo' },
  { value: 'zerado', label: 'Saldo zerado' },
  { value: 'em_transito', label: 'Em trânsito (romaneios separados)' },
];

function tipoValido(valor) {
  return TIPOS.some((t) => t.value === valor) ? valor : 'baixo';
}

function podeRelatorio(permissions) {
  if (permissions == null) return true;
  return Boolean(permissions.estoqueRelatorio || permissions.acessoGerencial);
}

export default function EstoqueSituacaoReport() {
  const permissions = useMemo(() => getAuthPermissoes(), []);
  const { estabelecimentoId, estabelecimentos } = useEstabelecimento();
  const [searchParams] = useSearchParams();
  const tipoInicial = tipoValido(searchParams.get('tipo') || 'baixo');

  const [tipo, setTipo] = useState(tipoInicial);
  const [filialId, setFilialId] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [fornecedorId, setFornecedorId] = useState('');
  const [termo, setTermo] = useState('');
  const [applied, setApplied] = useState({
    tipo: tipoInicial,
    filialId: '',
    categoriaId: '',
    fornecedorId: '',
    termo: '',
  });

  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filiais = useMemo(
    () => (estabelecimentos || []).map((e) => ({
      id: e.id,
      nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
    })),
    [estabelecimentos],
  );

  useEffect(() => {
    const loadCombos = async () => {
      try {
        const [catResp, fornResp] = await Promise.all([
          getAllCategories({ indice: 1, qtde: 300 }),
          getAllSuppliers({ indice: 1, qtde: 300 }),
        ]);
        setCategorias(catResp.data?.items || []);
        setFornecedores(fornResp.data?.items || fornResp.data || []);
      } catch {
        /* combos opcionais */
      }
    };
    loadCombos();
  }, []);

  useEffect(() => {
    if (!filialId && estabelecimentoId) {
      setFilialId(String(estabelecimentoId));
    }
  }, [estabelecimentoId, filialId]);

  useEffect(() => {
    const t = tipoValido(searchParams.get('tipo') || 'baixo');
    setTipo(t);
    setPage(1);
    setApplied((prev) => (prev.tipo === t ? prev : { ...prev, tipo: t }));
  }, [searchParams]);

  const carregar = useCallback(async () => {
    if (!podeRelatorio(permissions)) {
      setError('Sem permissão para relatórios de estoque.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: resp } = await getEstoqueSituacaoRelatorio({
        tipo: applied.tipo,
        estabelecimentoId: applied.filialId || undefined,
        categoriaId: applied.categoriaId || undefined,
        fornecedorId: applied.fornecedorId || undefined,
        termo: applied.termo || undefined,
        indice: page,
        qtde: 50,
      });
      setData({
        items: resp?.items || [],
        totalItems: resp?.totalItems || 0,
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível gerar o relatório.'));
    } finally {
      setLoading(false);
    }
  }, [applied, page, permissions]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aplicarFiltros = () => {
    setPage(1);
    setApplied({
      tipo,
      filialId,
      categoriaId,
      fornecedorId,
      termo: normalizeSearchTerm(termo),
    });
  };

  const exportarCsv = async () => {
    try {
      const { data: resp } = await getEstoqueSituacaoRelatorio({
        tipo: applied.tipo,
        estabelecimentoId: applied.filialId || undefined,
        categoriaId: applied.categoriaId || undefined,
        fornecedorId: applied.fornecedorId || undefined,
        termo: applied.termo || undefined,
        formato: 'csv',
      });
      const blob = new Blob([resp], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estoque_${applied.tipo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao exportar CSV.'));
    }
  };

  const exportarCsvLocal = () => {
    if (!data.items?.length) return;
    downloadCsv(
      `estoque_${applied.tipo}_pagina.csv`,
      data.items.map((row) => ({
        Codigo: row.codigo,
        Descricao: row.descricao,
        Categoria: row.categoria,
        Filial: row.estabelecimentoNome,
        Local: row.localNome,
        Saldo: row.saldo,
        EstoqueMinimo: row.estoqueMinimo,
        EstoqueIdeal: row.estoqueIdeal,
        EstoqueMaximo: row.estoqueMaximo,
        QtdEmTransito: row.qtdEmTransito,
      })),
    );
  };

  const { sorted: rowsOrdenados, orderBy, order, requestSort } = useClientSort(
    data.items || [],
    'descricao',
    'asc',
  );

  const totalPages = Math.max(1, Math.ceil((data.totalItems || 0) / 50));
  const tipoLabel = TIPOS.find((t) => t.value === applied.tipo)?.label || applied.tipo;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Situação de estoque"
        subtitle={`Relatório por filial — ${tipoLabel}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportarCsv}
              disabled={!podeRelatorio(permissions)}
            >
              Exportar CSV (completo)
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          >
            {TIPOS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Filial"
            value={filialId}
            onChange={(e) => setFilialId(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Todas permitidas</MenuItem>
            {filiais.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Categoria"
            value={categoriaId}
            onChange={(e) => setCategoriaId(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {categorias.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Fornecedor"
            value={fornecedorId}
            onChange={(e) => setFornecedorId(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {fornecedores.map((f) => (
              <MenuItem key={f.id} value={String(f.id)}>
                {f.nomeFantasia || f.razaoSocial || f.nome}
              </MenuItem>
            ))}
          </TextField>
          <BuscaProdutoCampo
            value={termo}
            onChange={setTermo}
            onSearch={aplicarFiltros}
            sx={{ minWidth: 280, flex: 1 }}
          />
          <Button variant="contained" onClick={aplicarFiltros}>
            Gerar
          </Button>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descricao" label="Produto" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="categoria" label="Categoria" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="estabelecimentoNome" label="Filial" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="localNome" label="Local" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="saldo" label="Saldo" orderBy={orderBy} order={order} onSort={requestSort} align="right" />
              <SortableTh id="estoqueMinimo" label="Mín." orderBy={orderBy} order={order} onSort={requestSort} align="right" />
              <SortableTh id="estoqueIdeal" label="Ideal" orderBy={orderBy} order={order} onSort={requestSort} align="right" />
              <SortableTh id="estoqueMaximo" label="Máx." orderBy={orderBy} order={order} onSort={requestSort} align="right" />
              {applied.tipo === 'em_transito' && (
                <SortableTh id="qtdEmTransito" label="Em trânsito" orderBy={orderBy} order={order} onSort={requestSort} align="right" />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={applied.tipo === 'em_transito' ? 10 : 9} align="center">
                  Nenhum registro para os filtros selecionados.
                </TableCell>
              </TableRow>
            ) : (
              rowsOrdenados.map((row) => (
                <TableRow key={`${row.produtoId}-${row.estabelecimentoId}-${row.localEstoqueId || 0}`} hover>
                  <TableCell>{row.codigo}</TableCell>
                  <TableCell>{row.descricao}</TableCell>
                  <TableCell>{row.categoria}</TableCell>
                  <TableCell>{row.estabelecimentoNome}</TableCell>
                  <TableCell>{row.localNome || 'Estoque principal'}</TableCell>
                  <TableCell align="right">{row.saldo}</TableCell>
                  <TableCell align="right">{row.estoqueMinimo}</TableCell>
                  <TableCell align="right">{row.estoqueIdeal}</TableCell>
                  <TableCell align="right">{row.estoqueMaximo}</TableCell>
                  {applied.tipo === 'em_transito' && (
                    <TableCell align="right">{row.qtdEmTransito}</TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
          {data.totalItems} registro(s)
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {data.items.length > 0 && (
            <Button size="small" onClick={exportarCsvLocal}>
              CSV desta página
            </Button>
          )}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
